// Shared "All calls from this number" popup — used by the Calls table
// (count button in the History column) and the Call detail page button.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  X, PhoneOutgoing, PhoneIncoming, Loader2, Clock, History, ArrowUpRight,
} from "lucide-react";

const fmtDur = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60), r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
};
const dateShort = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return "—"; }
};
const bandTone = (score) =>
  score == null ? "var(--t2-grey4)" : score >= 75 ? "var(--t2-teal)" : score >= 50 ? "var(--t2-brass)" : "var(--t2-red)";

export default function CallsFromNumberModal({ number, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/calls/by-number?number=${encodeURIComponent(number)}`);
        if (!cancelled) setData(data);
      } catch { if (!cancelled) setData({ calls: [], summary: { total: 0, inbound: 0, outbound: 0 } }); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [number]);

  const s = data?.summary || { total: 0, inbound: 0, outbound: 0 };

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="calls-by-number-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "grid", placeItems: "center", zIndex: 80 }}>
      <div onMouseDown={(e) => e.stopPropagation()}
        style={{ width: 640, maxWidth: "94vw", maxHeight: "82vh", display: "flex", flexDirection: "column",
          background: "var(--t2-surface2)", border: "1px solid var(--t2-border)", borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "16px 18px", borderBottom: "1px solid var(--t2-border)" }}>
          <div>
            <div className="t2-page-eyebrow" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <History className="w-3 h-3" /> Calls from this number
            </div>
            <div className="t2-mono" style={{ fontSize: 15, fontWeight: 800, color: "var(--t2-white)", marginTop: 4 }}>{number}</div>
            {!loading && (
              <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey3)", marginTop: 3 }}>
                <span style={{ color: "var(--t2-white)" }}>{s.total}</span> total ·
                <span style={{ color: "var(--t2-teal)" }}> {s.inbound}</span> in ·
                <span style={{ color: "var(--t2-orange)" }}> {s.outbound}</span> out
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="calls-by-number-close"
            style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer", padding: 4 }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="t2-scroll" style={{ overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--t2-grey3)" }}>
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
            </div>
          )}
          {!loading && (data?.calls || []).length === 0 && (
            <div className="t2-mono" style={{ padding: 30, textAlign: "center", color: "var(--t2-grey4)", fontSize: 12 }}>
              No other calls from this number.
            </div>
          )}
          {!loading && (data?.calls || []).map((c) => {
            const isOut = c.direction === "Outbound";
            const DirIcon = isOut ? PhoneOutgoing : PhoneIncoming;
            return (
              <button key={c.id} onClick={() => { onClose?.(); navigate(`/lab/calls/${c.id}`); }}
                data-testid={`calls-by-number-row-${c.id}`}
                style={{ textAlign: "left", background: "var(--t2-surface)", border: "1px solid var(--t2-border2)",
                  borderRadius: 9, padding: "10px 12px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span className="t2-mono" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: isOut ? "var(--t2-orange)" : "var(--t2-teal)", fontWeight: 700 }}>
                    <DirIcon className="w-3 h-3" /> {c.direction || "—"}
                  </span>
                  <span className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey2)" }}>{dateShort(c.when)}</span>
                  <span className="t2-mono" style={{ fontSize: 11, color: "var(--t2-brass)", fontWeight: 700 }}>{c.agent || "—"}</span>
                  <span className="t2-mono" style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "var(--t2-grey2)" }}>
                    <Clock className="w-3 h-3" /> {fmtDur(c.duration)}
                  </span>
                  {c.score != null && (
                    <span className="t2-mono" style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 800, color: bandTone(c.score) }}>
                      ★ {c.score}
                    </span>
                  )}
                  <ArrowUpRight className="w-3.5 h-3.5" style={{ color: "var(--t2-grey4)" }} />
                </div>
                {(c.category || c.summary) && (
                  <div style={{ fontSize: 12, color: "var(--t2-grey1)", lineHeight: 1.4 }}>
                    {c.category ? <span style={{ color: "var(--t2-violet, #a78bfa)", fontWeight: 700 }}>{c.category} · </span> : null}
                    {c.summary ? (c.summary.length > 150 ? c.summary.slice(0, 150) + "…" : c.summary) : ""}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
